import { ReactNode } from 'react';
import { Options, documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { BLOCKS, Document, INLINES } from '@contentful/rich-text-types';

const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

export enum Category {
  Feature = 'New Feature',
  Partnerships = 'Partnerships',
  Help = 'Help and Advice',
}

export type Author = {
  name: string;
  title: string;
  thumbnail: {
    url: string;
  };
};

type Block = {
  sys: {
    id: string;
  };
  title: string;
  description: string;
  contentType: string;
  fileName: string;
  size: number;
  url: string;
  width: number;
  height: number;
};

type Assets = {
  block: Block[];
};

export type BlogPost = {
  title: string;
  category: Category;
  date: string;
  slug: string;
  author: Author;
  thumbnail?: {
    url: string;
  };
  article?: {
    json: Document;
    links?: {
      assets: Assets;
    };
  };
};

export const getCategoryClassName = (category: Category): string => {
  switch (category) {
    case Category.Feature:
      return 'feature';
    case Category.Partnerships:
      return 'partnerships';
    case Category.Help:
      return 'help';
    default:
      return 'feature';
  }
};

export const getFormattedDate = (date: string) => {
  const d = new Date(date);
  const year = d.getFullYear();
  const month = months[d.getMonth()];
  const day = d.getDate();
  const formattedDay = day < 10 ? `0${day}` : day;

  return `${formattedDay} ${month} ${year}`;
};

export const renderRichText = (document: Document, assets: Assets): ReactNode => {
  const options: Options = {
    renderNode: {
      [BLOCKS.QUOTE]: (node, children) => <blockquote>{children}</blockquote>,
      [BLOCKS.PARAGRAPH]: (node, children) => <p className="p-body mb-4 text-inherit">{children}</p>,
      [BLOCKS.HEADING_2]: (node, children) => <h3 className="h3 mb-6 text-black">{children}</h3>,
      [BLOCKS.HEADING_3]: (node, children) => <h5 className="h5 mb-6">{children}</h5>,
      [BLOCKS.EMBEDDED_ASSET]: (node, children) => {
        // TODO refactor this into a separate component
        const id = node.data.target.sys.id;
        const block = assets?.block?.find((b) => b.sys.id === id);
        if (!block) return null;

        if (block.contentType.indexOf('image') > -1) {
          // eslint-disable-next-line @next/next/no-img-element
          return <img src={block.url} alt={block.description} />;
        }
        return <>{children}</>;
      },

      [INLINES.ENTRY_HYPERLINK]: (node, children) => {
        console.log(children, node);
        return <>{children}</>;
      },
      [INLINES.ASSET_HYPERLINK]: (node, children) => {
        console.log(children, node);
        return <>{children}</>;
      },
      [INLINES.EMBEDDED_ENTRY]: (node, children) => {
        console.log(children, node);
        return <>{children}</>;
      },
      [INLINES.HYPERLINK]: (node, children) => {
        const uri = node.data.uri;

        return (
          <a className="text-brand-primary hover:underline" href={uri} target="_blank" rel="noreferrer">
            {children}
          </a>
        );
      },
      [BLOCKS.UL_LIST]: (node, children) => <ul className="ml-6 list-disc">{children}</ul>,
    },
  };

  return documentToReactComponents(document, options);
};

export const limitParagraphs = (limit: number, content: ReactNode) => {
  if (Array.isArray(content)) {
    const tmp = [...content];
    return tmp.splice(0, limit);
  } else return content;
};

export const sliceArticleInHalf = (content: ReactNode) => {
  if (Array.isArray(content)) {
    const half = content.length;

    return limitParagraphs(half, content);
  } else return content;
};
